import { plainToClass } from "class-transformer";
import { SiteHealthCategory, SiteHealthMetric, SiteHealthMetricScaling, SiteHealthMetricMetaData } from "./metric-tool.entities";
declare var $: any;
export class MetricToolPage {
    categories: SiteHealthCategory[];
    metrics: SiteHealthMetric[];
    scalings: SiteHealthMetricScaling[];
    variableNames: string[];
    variableMetadata: SiteHealthMetricMetaData[];

    categoriesTable: any;
    metricsTable: any;
    categoryManagementTable: any;
    metricManagementTable: any;

    editingCategoryId: number | null = null;
    editingMetricId: number | null = null;

    tempCategories: SiteHealthCategory[] = [];
    tempMetrics: SiteHealthMetric[] = [];

    constructor(categoriesJson: any, metricsJson: any, scalingsJson: any, variableNamesJson: any, variableMetadataJson: any) {
        $(() => {
            this.categories = plainToClass(SiteHealthCategory, <SiteHealthCategory[]>categoriesJson);
            this.metrics = plainToClass(SiteHealthMetric, <SiteHealthMetric[]>metricsJson);
            this.scalings = plainToClass(SiteHealthMetricScaling, <SiteHealthMetricScaling[]>scalingsJson);
            this.variableNames = variableNamesJson;
            this.variableMetadata = plainToClass(SiteHealthMetricMetaData, <SiteHealthMetricMetaData[]>variableMetadataJson);

            // Fill out metrics with category and scaling names
            this.fillMetrics();

            this.initializeCategoriesTable();
            this.initializeMetricsTable();
            this.populateDropdowns();

            // Adjust columns when tabs are switched
            $('input[name="metrrictoolviewby"]').on('change', (e) => {
                const id = $(e.currentTarget).attr('id');
                $('#categories-content, #metrics-content').removeClass('show active');
                if (id === 'categories-tab') {
                    $('#categories-content').addClass('show active');
                    this.categoriesTable.columns.adjust().draw();
                } else if (id === 'metrics-tab') {
                    $('#metrics-content').addClass('show active');
                    this.metricsTable.columns.adjust().draw();
                }
            });
            $('#category-management-modal').on('shown.bs.modal', () => {
                this.initializeCategoryManagementTable();
            });
            $('#metric-management-modal').on('shown.bs.modal', () => {
                this.initializeMetricManagementTable();
            });
        });
    }

    fillMetrics(): void {
        this.metrics.forEach((metric: SiteHealthMetric) => {
            const category = this.categories.find(c => c.Id === metric.SiteHealthCategoryId);
            if (category) {
                metric.CategoryName = category.Name;
            }
            const scaling = this.scalings.find(s => s.Id === metric.SiteHealthMetricScalingId);
            if (scaling) {
                metric.ScalingName = scaling.Name;
            }
        });
    }

    initializeCategoriesTable(): void {
        const activeCategories = this.categories.filter(c => !c.IsArchived);
        this.categoriesTable = $('#categories-table').DataTable({
            data: activeCategories,
            autoWidth: false,
            info: false,
            paging: true,
            pagingType: 'simple',
            pageLength: 50,
            searching: true,
            scrollX: true,
            scrollY: '70vh',
            scrollCollapse: true,
            order: [[3, 'asc']], // Sort by SortById
            columns: [
                { data: 'Name', className: 'text-left font-size12' },
                { data: 'TitleInfo', className: 'text-left font-size12' },
                { data: 'SafeName', className: 'text-left font-size12' },
                { data: 'SortById', className: 'text-center font-size12', visible: false },
                {
                    data: null,
                    className: 'text-center font-size12',
                    orderable: false,
                    render: (data: SiteHealthCategory) => {
                        return `<button class="btn btn-sm btn-link p-0" onclick="page.editCategory(${data.Id})" title="Edit">
                                    <i class="fas fa-pencil-alt"></i>
                                </button>`;
                    }
                }
            ]
        });
    }

    initializeMetricsTable(): void {
        const activeMetrics = this.metrics.filter(m => !m.IsArchived);
        this.metricsTable = $('#metrics-table').DataTable({
            data: activeMetrics,
            autoWidth: false,
            info: false,
            paging: true,
            pagingType: 'simple',
            pageLength: 50,
            searching: true,
            scrollX: true,
            scrollY: '70vh',
            scrollCollapse: true,
            order: [[2, 'asc'], [6, 'asc']], // Sort by Category, then SortById
            columns: [
                { data: 'Name', className: 'text-left font-size12' },
                { data: 'TitleInfo', className: 'text-left font-size12' },
                { data: 'CategoryName', className: 'text-left font-size12' },
                { data: 'VariableName', className: 'text-left font-size12' },
                {
                    data: 'IsPercent',
                    className: 'text-center font-size12',
                    render: (data: boolean) => data ? 'Yes' : 'No'
                },
                { data: 'ScalingName', className: 'text-left font-size12' },
                { data: 'SortById', className: 'text-center font-size12', visible: false },
                {
                    data: null,
                    className: 'text-center font-size12',
                    orderable: false,
                    render: (data: SiteHealthMetric) => {
                        return `<button class="btn btn-sm btn-link p-0 mr-2" onclick="page.editMetric(${data.Id})" title="Edit">
                                    <i class="fas fa-pencil-alt"></i>
                                </button>
                                <button class="btn btn-sm btn-link p-0" onclick="page.deleteMetric(${data.Id})" title="Delete">
                                    <i class="fas fa-trash-alt text-danger"></i>
                                </button>`;
                    }
                }
            ]
        });
    }

    populateDropdowns(): void {
        // Populate metric category dropdown
        const $categorySelect = $('#metric-category');
        $categorySelect.empty();
        $categorySelect.append('<option value="">-- Select Category --</option>');
        this.categories.forEach((category: SiteHealthCategory) => {
            $categorySelect.append(`<option value="${category.Id}">${category.Name}</option>`);
        });

        // Populate metric scaling dropdown
        const $scalingSelect = $('#metric-scaling');
        $scalingSelect.empty();
        $scalingSelect.append('<option value="">-- Select Scaling Type --</option>');
        this.scalings.forEach((scaling: SiteHealthMetricScaling) => {
            $scalingSelect.append(`<option value="${scaling.Id}">${scaling.Name}</option>`);
        });

        this.populateVariableDropdown();

        $('#metric-variable').off('change.metadataDescription').on('change.metadataDescription', () => {
            const selectedValue = $('#metric-variable').val() as string;
            const metadata = this.variableMetadata.find(m => m.VariableName2 === selectedValue);
            if (metadata) {
                $('#metric-description').val(metadata.Description);
                $('#metric-name').val(metadata.MetricName);
            }
        });

        $('#metric-threshold-count').off('change.thresholdCount').on('change.thresholdCount', () => {
            this.updateThresholdVisibility();
            this.updateScalingPreview();
        });

        $('#metric-scaling').off('change.scalingPreview').on('change.scalingPreview', () => {
            this.updateScalingPreview();
        });

    }

    showCategoryModal(categoryId?: number): void {
        this.editingCategoryId = categoryId || null;

        $('#category-validation-error').addClass('d-none');
        this.hideModalError('category-save-error');

        if (categoryId) {
            const category = this.categories.find(c => c.Id === categoryId);
            if (category) {
                $('#category-modal-title').text('Edit Category');
                $('#category-id').val(category.Id);
                $('#category-name').val(category.Name);
                $('#category-description').val(category.TitleInfo);
                $('#category-safename').val(category.SafeName);
            }
        } else {
            $('#category-modal-title').text('Add Category');
            ($('#category-form')[0] as HTMLFormElement).reset();
            $('#category-id').val('');
        }

        $('#category-modal').modal('show');
    }

    editCategory(categoryId: number): void {
        this.showCategoryModal(categoryId);
    }

    saveCategory(): void {
        if (!this.validateCategoryForm()) {
            return;
        }

        $('#category-validation-error').addClass('d-none');

        const categoryData = {
            Id: parseInt($('#category-id').val() as string) || 0,
            Name: $('#category-name').val(),
            TitleInfo: $('#category-description').val(),
            SafeName: $('#category-safename').val(),
            SortById: this.editingCategoryId ?
                this.categories.find(c => c.Id === this.editingCategoryId)?.SortById || 0 :
                Math.max(...this.categories.map(c => c.SortById), 0) + 1
        };
        $('#saveCategoryBtn').prop('disabled', true);
        $('#categorySaveSpinner').show();
        $.ajax({
            url: `/secure/reporting/site-health/metric-tool/metric-tool?handler=SaveCategory`,
            type: 'POST',
            headers: {
                "RequestVerificationToken": $('input[name="__RequestVerificationToken"]').val(),
                "Content-Type": "application/json"
            },
            data: JSON.stringify(categoryData),
            dataType: 'json',
            success: (response) => {
                if (response && response.success && response.categories) {
                    // Convert camelCase to PascalCase
                    const pascalCaseCategories = this.mapKeysToPascalCase(response.categories);
                    this.categories = plainToClass(SiteHealthCategory, <SiteHealthCategory[]>pascalCaseCategories);

                    // Refresh the categories table
                    this.categoriesTable.clear();
                    this.categoriesTable.rows.add(this.categories);
                    this.categoriesTable.draw();

                    // Refresh dropdowns in metric modal
                    this.populateDropdowns();

                    $('#category-modal').modal('hide');
                } else {
                    this.showModalError('category-save-error', 'Error saving category: ' + (response.error || 'Unknown error'));
                }
            },
            error: (xhr, status, error) => {
                console.error('AJAX Error:', error);
                this.showModalError('category-save-error', 'Failed to save category. Please try again.');
            },
            complete: () => {
                $('#saveCategoryBtn').prop('disabled', false);
                $('#categorySaveSpinner').hide();
            }
        }).always(function () {
            $('#saveCategoryBtn').prop('disabled', false);
            $('#categorySaveSpinner').hide();
        });
    }

    updateScalingPreview(): void {
        const scalingId = parseInt($('#metric-scaling').val() as string);
        const count = parseInt($('#metric-threshold-count').val() as string);
        const scaling = scalingId ? this.scalings.find(s => s.Id === scalingId) : null;

        if (!scaling || isNaN(count)) {
            $('#scaling-preview').addClass('d-none');
            return;
        }

        const flip = scalingId != 2;
        const flipScore = (score: number) => flip ? 1 - score : score;

        const toColor = (score: number): string => {
            let r: number, g: number, b: number;

            const red = { r: 240, g: 125, b: 125 };
            const green = { r: 127, g: 184, b: 127 };
            const white = { r: 255, g: 255, b: 255 };

            if (score < 0.5) {

                const t = score / 0.5;
                r = Math.round(red.r + (white.r - red.r) * t);
                g = Math.round(red.g + (white.g - red.g) * t);
                b = Math.round(red.b + (white.b - red.b) * t);
            } else {

                const t = (score - 0.5) / 0.5;
                r = Math.round(white.r + (green.r - white.r) * t);
                g = Math.round(white.g + (green.g - white.g) * t);
                b = Math.round(white.b + (green.b - white.b) * t);
            }

            return `rgb(${r},${g},${b})`;
        };

        const colorName = (score: number): string =>
            score <= 0.05 ? 'Red' : score >= 0.95 ? 'Green' : (score >= 0.45 && score <= 0.55) ? 'White' : score < 0.45 ? 'Light Red' : 'Light Green';

        //threshold count 2: threshold 1 > white > threshold 2
        //threshold count 3: threshold 1 > threshold 2 = white > threshold 3
        //threshold count 4: threshold 1 > threshold 2 = white > threshold 3 = white > threshold 4
        const W = toColor(0.5);
        const gradientStops = count === 2
            ? `${toColor(flipScore(0))} 0%, ${W} 50%, ${toColor(flipScore(1))} 100%`
            : count === 3
                ? `${toColor(flipScore(0))} 0%, ${W} 50%, ${toColor(flipScore(1))} 100%`
                : `${toColor(flipScore(0))} 0%, ${toColor(flipScore(0.33))} 33%, ${W} 33%, ${W} 67%, ${toColor(flipScore(0.67))} 67%, ${toColor(flipScore(1))} 100%`;

        $('#scaling-preview-bar').css('background', `linear-gradient(to right, ${gradientStops})`);

        // threshold labels
        const thresholdScores = count === 2 ? [flipScore(0), flipScore(1)] : count === 3 ? [flipScore(0), flipScore(0.5), flipScore(1)] : [flipScore(0), flipScore(0.33), flipScore(0.67), flipScore(1)];
        $('#scaling-preview-labels').html(thresholdScores.map((score, i) =>
            `<span class="scaling-preview-label">
                <span class="scaling-preview-swatch" style="background:${toColor(score)};"></span>
                <span class="scaling-preview-label-text">T${i + 1}: ${colorName(score)}</span>
            </span>`
        ).join(''));

        $('#scaling-preview').removeClass('d-none');
    }

    updateThresholdVisibility(): void {
        const count = parseInt($('#metric-threshold-count').val() as string);
        $('#metric-threshold3-group').toggle(count >= 3);
        $('#metric-threshold4-group').toggle(count >= 4);
        if (count < 3) $('#metric-threshold3').val('');
        if (count < 4) $('#metric-threshold4').val('');
    }

    showMetricModal(metricId?: number): void {
        this.editingMetricId = metricId || null;

        $('#validation-error').addClass('d-none');
        this.hideModalError('metric-save-error');

        // Build exclusion list: variables used by non-archived metrics, minus the one being edited
        const usedVariables = this.metrics
            .filter(m => !m.IsArchived && m.Id !== (metricId || 0))
            .map(m => m.VariableName)
            .filter(v => !!v);
        this.populateVariableDropdown(usedVariables);

        if (metricId) {
            const metric = this.metrics.find(m => m.Id === metricId);
            if (metric) {
                const count = metric.ScalingValueCount || 2;

                $('#metric-modal-title').text('Edit Metric');
                $('#metric-id').val(metric.Id);
                $('#metric-name').val(metric.Name);
                $('#metric-description').val(metric.TitleInfo);
                $('#metric-category').val(metric.SiteHealthCategoryId);
                $('#metric-variable').val(metric.VariableName);
                $('#metric-scaling').val(metric.SiteHealthMetricScalingId);
                $('#metric-ispercent').prop('checked', metric.IsPercent);
                $('#metric-threshold-count').val(count.toString());
                $('#metric-threshold1').val(metric.ScalingValue1 !== null ? metric.ScalingValue1 : '');
                $('#metric-threshold2').val(metric.ScalingValue2 !== null ? metric.ScalingValue2 : '');
                $('#metric-threshold3').val(metric.ScalingValue3 !== null ? metric.ScalingValue3 : '');
                $('#metric-threshold4').val(metric.ScalingValue4 !== null ? metric.ScalingValue4 : '');
            }
        } else {
            $('#metric-modal-title').text('Add Metric');
            ($('#metric-form')[0] as HTMLFormElement).reset();
            $('#metric-id').val('');
            $('#metric-threshold-count').val('2');
        }

        this.updateThresholdVisibility();
        this.updateScalingPreview();
        $('#metric-modal').modal('show');
    }

    editMetric(metricId: number): void {
        this.showMetricModal(metricId);
    }

    deleteMetric(metricId: number): void {
        const metric = this.metrics.find(m => m.Id === metricId);
        if (!metric) return;

        if (!confirm(`Are you sure you want to delete the metric "${metric.Name}"? This cannot be undone.`)) {
            return;
        }

        $.ajax({
            url: `/secure/reporting/site-health/metric-tool/metric-tool?handler=DeleteMetric`,
            type: 'POST',
            headers: {
                "RequestVerificationToken": $('input[name="__RequestVerificationToken"]').val(),
                "Content-Type": "application/json"
            },
            data: JSON.stringify({ Id: metricId }),
            dataType: 'json',
            success: (response) => {
                if (response && response.success && response.metrics) {
                    const pascalCaseMetrics = this.mapKeysToPascalCase(response.metrics);
                    this.metrics = plainToClass(SiteHealthMetric, <SiteHealthMetric[]>pascalCaseMetrics);

                    this.fillMetrics();

                    const activeMetrics = this.metrics.filter(m => !m.IsArchived);
                    this.metricsTable.clear();
                    this.metricsTable.rows.add(activeMetrics);
                    this.metricsTable.draw();
                } else {
                    alert('Error deleting metric: ' + (response?.error || 'Unknown error'));
                }
            },
            error: (xhr, status, error) => {
                console.error('AJAX Error:', error);
                alert('Failed to delete metric. Please try again.');
            }
        });
    }

    saveMetric(): void {
        if (!this.validateMetricForm()) {
            return;
        }

        $('#validation-error').addClass('d-none');

        const threshold1 = $('#metric-threshold1').val();
        const threshold2 = $('#metric-threshold2').val();
        const threshold3 = $('#metric-threshold3').val();
        const threshold4 = $('#metric-threshold4').val();

        const metricData = {
            Id: parseInt($('#metric-id').val() as string) || 0,
            Name: $('#metric-name').val(),
            TitleInfo: $('#metric-description').val(),
            SiteHealthCategoryId: parseInt($('#metric-category').val() as string),
            VariableName: $('#metric-variable').val(),
            SiteHealthMetricScalingId: parseInt($('#metric-scaling').val() as string),
            IsPercent: $('#metric-ispercent').is(':checked'),
            ScalingValueCount: parseInt($('#metric-threshold-count').val() as string),
            ScalingValue1: threshold1 ? parseFloat(threshold1 as string) : null,
            ScalingValue2: threshold2 ? parseFloat(threshold2 as string) : null,
            ScalingValue3: threshold3 ? parseFloat(threshold3 as string) : null,
            ScalingValue4: threshold4 ? parseFloat(threshold4 as string) : null,
            SortById: this.editingMetricId ?
                this.metrics.find(m => m.Id === this.editingMetricId)?.SortById || 0 :
                Math.max(...this.metrics.filter(m => m.SiteHealthCategoryId === parseInt($('#metric-category').val() as string)).map(m => m.SortById), 0) + 1
        };

        $('#saveMetricBtn').prop('disabled', true);
        $('#metricSaveSpinner').show();
        $.ajax({
            url: `/secure/reporting/site-health/metric-tool/metric-tool?handler=SaveMetric`,
            type: 'POST',
            headers: {
                "RequestVerificationToken": $('input[name="__RequestVerificationToken"]').val(),
                "Content-Type": "application/json"
            },
            data: JSON.stringify(metricData),
            dataType: 'json',
            success: (response) => {
                if (response && response.success && response.metrics) {
                    // Convert camelCase to PascalCase
                    const pascalCaseMetrics = this.mapKeysToPascalCase(response.metrics);
                    this.metrics = plainToClass(SiteHealthMetric, <SiteHealthMetric[]>pascalCaseMetrics);

                    // Fill out metrics with category and scaling names
                    this.fillMetrics();

                    // Refresh the metrics table
                    this.metricsTable.clear();
                    this.metricsTable.rows.add(this.metrics);
                    this.metricsTable.draw();

                    $('#metric-modal').modal('hide');
                } else {
                    this.showModalError('metric-save-error', 'Error saving metric: ' + (response.error || 'Unknown error'));
                }
            },
            error: (xhr, status, error) => {
                console.error('AJAX Error:', error);
                this.showModalError('metric-save-error', 'Failed to save metric. Please try again.');
            },
            complete: () => {
                $('#saveMetricBtn').prop('disabled', false);
                $('#metricSaveSpinner').hide();
            }
        }).always(function () {
            $('#saveMetricBtn').prop('disabled', false);
            $('#metricSaveSpinner').hide();
        });
    }

    validateCategoryForm(): boolean {
        const nameValue = $('#category-name').val();
        const safeNameValue = $('#category-safename').val();

        if (!nameValue || !safeNameValue) {
            $('#category-validation-error').removeClass('d-none');
            return false;
        }
        return true;
    }

    validateMetricForm(): boolean {
        const nameValue = $('#metric-name').val();
        const categoryValue = $('#metric-category').val();
        const variableValue = $('#metric-variable').val();
        const scalingValue = $('#metric-scaling').val();
        const threshold1Value = $('#metric-threshold1').val();
        const threshold2Value = $('#metric-threshold2').val();
        const thresholdCount = parseInt($('#metric-threshold-count').val() as string);
        const threshold3Value = $('#metric-threshold3').val();
        const threshold4Value = $('#metric-threshold4').val();

        const threshold3Valid = thresholdCount < 3 || !!threshold3Value;
        const threshold4Valid = thresholdCount < 4 || !!threshold4Value;

        if (!nameValue || !categoryValue || !variableValue || !scalingValue || !threshold1Value || !threshold2Value || !threshold3Valid || !threshold4Valid) {
            $('#validation-error').removeClass('d-none');
            return false;
        }
        return true;
    }

    populateVariableDropdown(excludeVariableNames: string[] = []): void {
        const $variableSelect = $('#metric-variable');
        $variableSelect.empty();
        $variableSelect.append('<option value="">-- Select Variable --</option>');
        this.variableNames.forEach((variableName: string) => {
            if (excludeVariableNames.includes(variableName)) return;
            const metadata = this.variableMetadata.find(m => m.VariableName2 === variableName);
            const label = metadata ? metadata.MetricName : variableName;
            $variableSelect.append(`<option value="${variableName}">${label}</option>`);
        });
    }

    private showModalError(errorDivId: string, message: string): void {
        const $error = $(`#${errorDivId}`);
        $error.text(message).removeClass('d-none');
    }

    private hideModalError(errorDivId: string): void {
        $(`#${errorDivId}`).addClass('d-none').text('');
    }

    //Helper function to convert object keys from camelCase to PascalCase
    private mapKeysToPascalCase(data: any[]): any[] {
        return data.map(item => {
            const newItem: any = {};
            Object.keys(item).forEach(key => {
                const pascalKey = key.charAt(0).toUpperCase() + key.slice(1);
                newItem[pascalKey] = item[key];
            });
            return newItem;
        });
    }

    // Management Modal Methods
    showCategoryManagementModal(): void {
        this.tempCategories = JSON.parse(JSON.stringify(this.categories));
        this.tempCategories = plainToClass(SiteHealthCategory, this.tempCategories);
        this.hideModalError('category-management-save-error');
        $('#category-management-modal').modal('show');
    }

    showMetricManagementModal(): void {
        this.tempMetrics = JSON.parse(JSON.stringify(this.metrics));
        this.tempMetrics = plainToClass(SiteHealthMetric, this.tempMetrics);
        this.hideModalError('metric-management-save-error');
        $('#metric-management-modal').modal('show');
    }

    initializeCategoryManagementTable(): void {
        if (this.categoryManagementTable) {
            this.categoryManagementTable.destroy();
        }

        // Sort: non-archived first, then by SortById
        const sortedCategories = [...this.tempCategories].sort((a, b) => {
            if (a.IsArchived !== b.IsArchived) {
                return a.IsArchived ? 1 : -1;
            }
            return a.SortById - b.SortById;
        });

        this.categoryManagementTable = $('#category-management-table').DataTable({
            data: sortedCategories,
            autoWidth: false,
            info: false,
            paging: false,
            searching: false,
            scrollX: true,
            scrollY: '60vh',
            scrollCollapse: true,
            ordering: false,
            columns: [
                { data: 'Name', className: 'text-left font-size12' },
                { data: 'TitleInfo', className: 'text-left font-size12' },
                { data: 'SafeName', className: 'text-left font-size12' },
                {
                    data: 'IsArchived',
                    className: 'text-center font-size12',
                    render: (data: boolean) => {
                        if (data) {
                            return '<span class="badge badge-secondary">Archived</span>';
                        }
                        return '<span class="badge badge-success">Active</span>';
                    }
                },
                {
                    data: null,
                    className: 'text-center font-size12',
                    orderable: false,
                    width: '120px',
                    render: (data: SiteHealthCategory, type, row, meta) => {
                        const rowIndex = meta.row;
                        const isFirst = (rowIndex === 0 || sortedCategories[rowIndex - 1].IsArchived !== data.IsArchived);
                        const isLast = (rowIndex === sortedCategories.length - 1 || sortedCategories[rowIndex + 1].IsArchived !== data.IsArchived);

                        return `
                            ${!isFirst ?
                                `<button class="btn btn-sm btn-link p-0 mr-2" onclick="page.moveCategoryUp(${data.Id})" title="Move Up">
                                    <i class="fas fa-arrow-up text-primary"></i>
                                </button>` : ``
                            }
                            ${!isLast ?
                                `<button class="btn btn-sm btn-link p-0 mr-2" onclick="page.moveCategoryDown(${data.Id})" title="Move Down">
                                    <i class="fas fa-arrow-down text-primary"></i>
                                </button>` : ``
                            }
                            ${!data.IsArchived ?
                                `<button class="btn btn-sm btn-link p-0" onclick="page.toggleCategoryArchive(${data.Id}, false)" title="Archive">
                                    <i class="fas fa-archive text-warning"></i>
                                </button>` :
                                `<button class="btn btn-sm btn-link p-0" onclick="page.toggleCategoryArchive(${data.Id}, true)" title="Restore">
                                    <i class="fas fa-undo text-success"></i>
                                </button>`
                            }
                        `;
                    }
                }
            ]
        });
    }

    initializeMetricManagementTable(): void {
        if (this.metricManagementTable) {
            this.metricManagementTable.destroy();
        }

        // Sort: non-archived first, then by SortById
        const sortedMetrics = [...this.tempMetrics].sort((a, b) => {
            if (a.IsArchived !== b.IsArchived) {
                return a.IsArchived ? 1 : -1;
            }
            return a.SortById - b.SortById;
        });

        this.metricManagementTable = $('#metric-management-table').DataTable({
            data: sortedMetrics,
            autoWidth: false,
            info: false,
            paging: false,
            searching: false,
            scrollX: true,
            scrollY: '60vh',
            scrollCollapse: true,
            ordering: false,
            columns: [
                { data: 'Name', className: 'text-left font-size12' },
                { data: 'TitleInfo', className: 'text-left font-size12' },
                { data: 'CategoryName', className: 'text-left font-size12' },
                { data: 'VariableName', className: 'text-left font-size12' },
                {
                    data: 'IsArchived',
                    className: 'text-center font-size12',
                    render: (data: boolean) => {
                        if (data) {
                            return '<span class="badge badge-secondary">Archived</span>';
                        }
                        return '<span class="badge badge-success">Active</span>';
                    }
                },
                {
                    data: null,
                    className: 'text-center font-size12',
                    orderable: false,
                    width: '120px',
                    render: (data: SiteHealthMetric, type, row, meta) => {
                        const rowIndex = meta.row;
                        const isFirst = (rowIndex === 0 || sortedMetrics[rowIndex - 1].IsArchived !== data.IsArchived);
                        const isLast = (rowIndex === sortedMetrics.length - 1 || sortedMetrics[rowIndex + 1].IsArchived !== data.IsArchived);

                        return `
                            ${!isFirst ?
                                `<button class="btn btn-sm btn-link p-0 mr-2" onclick="page.moveMetricUp(${data.Id})" title="Move Up">
                                    <i class="fas fa-arrow-up text-primary"></i>
                                </button>` : ``
                            }
                            ${!isLast ?
                                `<button class="btn btn-sm btn-link p-0 mr-2" onclick="page.moveMetricDown(${data.Id})" title="Move Down">
                                    <i class="fas fa-arrow-down text-primary"></i>
                                </button>` : ``
                            }
                            ${!data.IsArchived ?
                                `<button class="btn btn-sm btn-link p-0" onclick="page.toggleMetricArchive(${data.Id}, false)" title="Archive">
                                    <i class="fas fa-archive text-warning"></i>
                                </button>` :
                                `<button class="btn btn-sm btn-link p-0" onclick="page.toggleMetricArchive(${data.Id}, true)" title="Restore">
                                    <i class="fas fa-undo text-success"></i>
                                </button>`
                            }
                        `;
                    }
                }
            ]
        });
    }

    moveCategoryUp(categoryId: number): void {
        const category = this.tempCategories.find(c => c.Id === categoryId);
        if (!category) return;

        // Find categories in the same archive status group
        const sameGroupCategories = this.tempCategories
            .filter(c => c.IsArchived === category.IsArchived)
            .sort((a, b) => a.SortById - b.SortById);

        const currentIndex = sameGroupCategories.findIndex(c => c.Id === categoryId);
        if (currentIndex <= 0) return; // Already at top

        // Swap SortById with previous item
        const prevCategory = sameGroupCategories[currentIndex - 1];
        const tempSortById = category.SortById;
        category.SortById = prevCategory.SortById;
        prevCategory.SortById = tempSortById;

        // Refresh table
        this.initializeCategoryManagementTable();
    }

    moveCategoryDown(categoryId: number): void {
        const category = this.tempCategories.find(c => c.Id === categoryId);
        if (!category) return;

        // Find categories in the same archive status group
        const sameGroupCategories = this.tempCategories
            .filter(c => c.IsArchived === category.IsArchived)
            .sort((a, b) => a.SortById - b.SortById);

        const currentIndex = sameGroupCategories.findIndex(c => c.Id === categoryId);
        if (currentIndex < 0 || currentIndex >= sameGroupCategories.length - 1) return; // Already at bottom

        // Swap SortById with next item
        const nextCategory = sameGroupCategories[currentIndex + 1];
        const tempSortById = category.SortById;
        category.SortById = nextCategory.SortById;
        nextCategory.SortById = tempSortById;

        // Refresh table
        this.initializeCategoryManagementTable();
    }

    moveMetricUp(metricId: number): void {
        const metric = this.tempMetrics.find(m => m.Id === metricId);
        if (!metric) return;

        // Find metrics in the same archive status group
        const sameGroupMetrics = this.tempMetrics
            .filter(m => m.IsArchived === metric.IsArchived)
            .sort((a, b) => a.SortById - b.SortById);

        const currentIndex = sameGroupMetrics.findIndex(m => m.Id === metricId);
        if (currentIndex <= 0) return; // Already at top

        // Swap SortById with previous item
        const prevMetric = sameGroupMetrics[currentIndex - 1];
        const tempSortById = metric.SortById;
        metric.SortById = prevMetric.SortById;
        prevMetric.SortById = tempSortById;

        // Refresh table
        this.initializeMetricManagementTable();
    }

    moveMetricDown(metricId: number): void {
        const metric = this.tempMetrics.find(m => m.Id === metricId);
        if (!metric) return;

        // Find metrics in the same archive status group
        const sameGroupMetrics = this.tempMetrics
            .filter(m => m.IsArchived === metric.IsArchived)
            .sort((a, b) => a.SortById - b.SortById);

        const currentIndex = sameGroupMetrics.findIndex(m => m.Id === metricId);
        if (currentIndex < 0 || currentIndex >= sameGroupMetrics.length - 1) return; // Already at bottom

        // Swap SortById with next item
        const nextMetric = sameGroupMetrics[currentIndex + 1];
        const tempSortById = metric.SortById;
        metric.SortById = nextMetric.SortById;
        nextMetric.SortById = tempSortById;

        // Refresh table
        this.initializeMetricManagementTable();
    }

    toggleCategoryArchive(categoryId: number, currentlyArchived: boolean): void {
        const category = this.tempCategories.find(c => c.Id === categoryId);
        if (!category) return;

        if (currentlyArchived) {
            // Unarchiving - move to bottom of active list
            category.IsArchived = false;
            const activeCategories = this.tempCategories.filter(c => !c.IsArchived && c.Id !== categoryId);
            category.SortById = Math.max(...activeCategories.map(c => c.SortById), 0) + 1;
        } else {
            // Archiving - check if any metrics use this category
            const affectedMetrics = this.metrics.filter(m => m.SiteHealthCategoryId === categoryId && !m.IsArchived);
            if (affectedMetrics.length > 0) {
                if (!confirm(`This category has ${affectedMetrics.length} active metric(s). Are you sure you want to archive it?`)) {
                    return;
                }
            }

            category.IsArchived = true;
            const archivedCategories = this.tempCategories.filter(c => c.IsArchived && c.Id !== categoryId);
            category.SortById = Math.max(...archivedCategories.map(c => c.SortById), 0) + 1;
        }

        // Refresh table
        this.initializeCategoryManagementTable();
    }

    toggleMetricArchive(metricId: number, currentlyArchived: boolean): void {
        const metric = this.tempMetrics.find(m => m.Id === metricId);
        if (!metric) return;

        if (currentlyArchived) {
            // Unarchiving - move to bottom of active list
            metric.IsArchived = false;
            const activeMetrics = this.tempMetrics.filter(m => !m.IsArchived && m.Id !== metricId);
            metric.SortById = Math.max(...activeMetrics.map(m => m.SortById), 0) + 1;
        } else {
            // Archiving - move to bottom of archived list
            metric.IsArchived = true;
            const archivedMetrics = this.tempMetrics.filter(m => m.IsArchived && m.Id !== metricId);
            metric.SortById = Math.max(...archivedMetrics.map(m => m.SortById), 0) + 1;
        }

        // Refresh table
        this.initializeMetricManagementTable();
    }

    saveManagementChanges(type: 'category' | 'metric'): void {
        const items = type === 'category'
            ? this.tempCategories.map(c => ({ Id: c.Id, SortById: c.SortById, IsArchived: c.IsArchived }))
            : this.tempMetrics.map(m => ({ Id: m.Id, SortById: m.SortById, IsArchived: m.IsArchived }));

        const payload = {
            Type: type,
            Items: items
        };

        const $saveBtn = type === 'category' ? $('#saveManageCategoryBtn') : $('#saveManageMetricBtn');
        const $spinner = type === 'category' ? $('#manageCategorySaveSpinner') : $('#manageMetricSaveSpinner');
        $saveBtn.prop('disabled', true);
        $spinner.show();
        $.ajax({
            url: `/secure/reporting/site-health/metric-tool/metric-tool?handler=SaveManagementChanges`,
            type: 'POST',
            headers: {
                "RequestVerificationToken": $('input[name="__RequestVerificationToken"]').val(),
                "Content-Type": "application/json"
            },
            data: JSON.stringify(payload),
            dataType: 'json',
            success: (response) => {
                if (response && response.success) {
                    if (type === 'category' && response.categories) {
                        const pascalCaseCategories = this.mapKeysToPascalCase(response.categories);
                        this.categories = plainToClass(SiteHealthCategory, <SiteHealthCategory[]>pascalCaseCategories);

                        // Refresh main table
                        const activeCategories = this.categories.filter(c => !c.IsArchived);
                        this.categoriesTable.clear();
                        this.categoriesTable.rows.add(activeCategories);
                        this.categoriesTable.draw();

                        // Refresh dropdowns
                        this.populateDropdowns();

                        $('#category-management-modal').modal('hide');
                    } else if (type === 'metric' && response.metrics) {
                        const pascalCaseMetrics = this.mapKeysToPascalCase(response.metrics);
                        this.metrics = plainToClass(SiteHealthMetric, <SiteHealthMetric[]>pascalCaseMetrics);

                        // Fill metrics with category/scaling names
                        this.fillMetrics();

                        // Refresh main table
                        const activeMetrics = this.metrics.filter(m => !m.IsArchived);
                        this.metricsTable.clear();
                        this.metricsTable.rows.add(activeMetrics);
                        this.metricsTable.draw();

                        $('#metric-management-modal').modal('hide');
                    }
                } else {
                    const mgmtErrorId = type === 'category' ? 'category-management-save-error' : 'metric-management-save-error';
                    this.showModalError(mgmtErrorId, 'Error saving changes: ' + (response.error || 'Unknown error'));
                }
            },
            error: (xhr, status, error) => {
                console.error('AJAX Error:', error);
                const mgmtErrorId = type === 'category' ? 'category-management-save-error' : 'metric-management-save-error';
                this.showModalError(mgmtErrorId, 'Failed to save changes. Please try again.');
            },
            complete: () => {
                $saveBtn.prop('disabled', false);
                $spinner.hide();
            }
        }).always(function () {
            $saveBtn.prop('disabled', false);
            $spinner.hide();
        });
    }
}
